import { faMicrophone } from '@fortawesome/pro-solid-svg-icons';
import { FC } from 'react';

import { useTranslate } from '@growtherapy/i18n';
import {
  AccessibleSelect,
  Body,
  Button,
  Checkbox,
  Icon,
  InputControl,
  SelectOptionAtom,
  cn,
} from '@growtherapy/sprout-ui';

import { useTheme } from '../../hooks/useTheme';
import { NoAccessVariant } from '../../session/controls';
import { Theme } from '../../state/theme';
import { VolumeMonitor } from '../VolumeMonitor';
import { WithPassThroughProps } from './BlockedPermissionMessage';

export function AudioSettingsTab({
  blockedPermissionMessageComponent: BlockedPermissionMessage,
  microphoneIsBlocked,
  microphoneIsLoading,
  outputIsLoading,
  inputVolume,
  outputVolume,
  playTestAudio,
  inputOptions,
  outputOptions,
  setMicrophone,
  setOutputDevice,
  selectedMicrophone,
  selectedOutputDevice,
  audioIsPlaying,
  toggleNoiseCancellation,
  noiseCancellationIsEnabled,
  isNoiseCancellationSupported,
}: {
  /** Shown when access is blocked. The wrapper passes the app wrapper; stories and tests pass the pure message. */
  blockedPermissionMessageComponent: FC<WithPassThroughProps>;
  microphoneIsBlocked: boolean;
  microphoneIsLoading?: boolean;
  outputIsLoading?: boolean;
  audioOutputDeviceId?: string;
  inputVolume: number;
  outputVolume: number;
  playTestAudio: () => void;
  inputOptions: SelectOptionAtom[];
  outputOptions: SelectOptionAtom[];
  setMicrophone: (selectedId: string) => void;
  setOutputDevice: (selectedId: string) => void | Promise<void>;
  selectedMicrophone?: string;
  selectedOutputDevice?: string;
  audioIsPlaying?: boolean;
  toggleNoiseCancellation?: () => Promise<void> | void;
  noiseCancellationIsEnabled?: boolean;
  isNoiseCancellationSupported?: boolean;
}) {
  const t = useTranslate();
  const { theme } = useTheme();

  const isDarkTheme = theme === Theme.DARK;
  const shouldDisableFields = microphoneIsBlocked || microphoneIsLoading;

  return (
    <>
      <div className='pb-6 border-b border-neutral-500 rebrand:border-border-subtle'>
        {outputOptions.length > 0 && (
          <InputControl
            disabled={outputIsLoading}
            label={t('telehealth-core:settings.speakers')}
            labelClassName={cn({
              'rebrand:text-text-inverse': isDarkTheme,
            })}
          >
            <AccessibleSelect
              onChange={setOutputDevice}
              options={outputOptions}
              value={selectedOutputDevice ?? outputOptions[0]?.value}
            />
          </InputControl>
        )}
        <div className='flex justify-between'>
          <Button use='secondary' size='sm' onClick={() => playTestAudio()} disabled={audioIsPlaying}>
            {t('telehealth-core:settings.testSpeakers')}
          </Button>
          <VolumeMonitor volume={outputVolume} />
        </div>
      </div>
      <div className='border-t border-neutral-200 pt-6 rebrand:border-border-subtle'>
        <InputControl
          label={t('telehealth-core:settings.microphone')}
          disabled={shouldDisableFields}
          labelClassName={cn({
            'rebrand:text-text-inverse': isDarkTheme,
          })}
        >
          <AccessibleSelect
            options={inputOptions}
            value={selectedMicrophone ?? inputOptions[0]?.value}
            onChange={(value: string) => setMicrophone(value)}
            placeholder={
              microphoneIsBlocked ? t('telehealth-core:settings.noAccess') : t('telehealth-core:settings.select')
            }
          />
        </InputControl>
        <div className='flex justify-between pb-6'>
          <Body.Small
            emphasis
            className={cn('text-small font-medium rebrand:text-text-default text-neutral-700', {
              'rebrand:text-text-inverse': isDarkTheme,
            })}
            as='h3'
          >
            <Icon aria-hidden icon={faMicrophone} className='mr-2' />
            {t('telehealth-core:settings.speakToTest')}
          </Body.Small>
          <VolumeMonitor volume={inputVolume} />
        </div>
        {microphoneIsBlocked && (
          <BlockedPermissionMessage
            noAccessVariant={NoAccessVariant.Microphone}
            className={cn({
              'rebrand:bg-surface-inverseEmphasis': isDarkTheme,
            })}
          />
        )}
        {isNoiseCancellationSupported && (
          <InputControl aria-label={t('telehealth-core:settings.toggleNoiseCancellation')}>
            <Checkbox
              onChange={() => toggleNoiseCancellation?.()}
              checked={noiseCancellationIsEnabled}
              label={t('telehealth-core:settings.reduceBackgroundNoise')}
              textClassName={cn({
                'rebrand:text-text-inverse': isDarkTheme,
              })}
              iconClassName={cn({
                'rebrand:text-text-inverse': isDarkTheme,
              })}
            />
          </InputControl>
        )}
      </div>
    </>
  );
}
